import Config from 'config';
import React from 'react';
import { Box, Grid } from '@apia/theme';
import { notificationsSelector } from '../../../../classes/focusController';
import useContext from '../../../../hooks/useContext';
import IProperty from '../../../../JSApi/constants/IProperty';
import { IDataGridField } from '../../../../JSApi/fields/DataGridField';
import store from '../../../../store';
import {
  hasSucceedValidation,
  previousValidationActions,
} from '../../../../store/previousValidationSlice';
import { TApiaFieldPropertiesObj, TApiaForm } from '../../../../types';
import {
  getFocusSelector,
  notUnfocusableSelector,
} from '../../../../utils/dom';
import { ConfirmModal } from '../../../common';
import { ValidationContext } from '../../../common/previousValidation/ValidationContext';
import fieldsStore from '../fieldsStore/fieldsStore';
import conf from '../../../../classes/configController';
import { getLabel } from '@apia/util';

export interface IApiaEditGridForm {
  children: React.ReactNode;
  formId: string;
  formName: string;
  formTitle: string;
  frmProps: TApiaFieldPropertiesObj;
  formData: TApiaForm;
}

const validate = async (form: string) => {
  const fieldStoreResult = await fieldsStore.validate();
  if (fieldStoreResult !== true) return false;

  const result = await store
    .dispatch(previousValidationActions.validate({ form }))
    .unwrap();

  if (hasSucceedValidation(result)) {
    return result;
  }

  return false;
};

const ApiaEditGridForm = ({
  formId,
  formName,
  formTitle,
  children,
  frmProps,
  formData,
}: IApiaEditGridForm): JSX.Element => {
  const cancellable = conf('execution.components.gridEditionModal.cancellable');

  const { form } = useContext(ValidationContext);
  const handleEditableFormConfirm = React.useCallback(
    async (shouldValidate = true) => {
      if (shouldValidate) {
        const validationResult = await validate(form);
        if (!validationResult) return false;
      }
      return true;
    },
    [form],
  );
  const initialFocusGetter = React.useCallback((modal: HTMLElement) => {
    return modal?.querySelector(
      getFocusSelector(
        `${notUnfocusableSelector}:not(.modal__closeButton):not(${notificationsSelector})`,
      ),
    ) as HTMLElement;
  }, []);
  const confirmProps = React.useMemo(
    () => ({
      confirmButtonText: cancellable
        ? Config.BTN_CLOSE
        : getLabel('btnCon').text,
    }),
    [cancellable],
  );
  const handleOnExited = React.useCallback(() => {
    const editableDataGridId =
      window.frameElement?.getAttribute('data-grid-id') || '';
    const editableGridFormName =
      window.frameElement?.getAttribute('data-grid-form-name') || '';
    const editableGridFormType =
      window.frameElement?.getAttribute('data-grid-form-type') || 'P';

    const ApiaFunctions = window.parent?.ApiaFunctions;
    const gridForm = ApiaFunctions?.getForm(
      editableGridFormName,
      editableGridFormType,
    );
    const grid = gridForm?.getFieldById(editableDataGridId) as IDataGridField;
    // refresh grid and close portal/iframe
    grid?.closeForm();
  }, []);
  const sx = React.useMemo(
    () => ({
      backgroundColor: 'transparent',
      '.modal__main': { maxWidth: '95vw' },
    }),
    [],
  );
  const gridSx = React.useMemo(
    () => ({
      variant: !frmProps[IProperty.PROPERTY_FORM_READONLY]
        ? 'grids.execution.form'
        : 'grids.execution.formReadOnly',
      gridTemplateColumns: `repeat(${formData?.cols ?? 4},1fr)`,
    }),
    [formData?.cols, frmProps],
  );

  return (
    <ConfirmModal
      confirmProps={confirmProps}
      initialFocusGetter={initialFocusGetter}
      onConfirm={handleEditableFormConfirm}
      onExited={handleOnExited}
      sx={sx}
      title={formTitle}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={cancellable}
      hideCloseButton={!cancellable}
      size="editGrid"
    >
      <Box
        role="form"
        id={formId}
        className={`confirm__frm--${formName}`}
        data-form-name={formName}
        data-form-title={formTitle}
        aria-label={formTitle}
        data-form-readonly={frmProps[IProperty.PROPERTY_FORM_READONLY]}
        data-form-collapsed={frmProps[IProperty.PROPERTY_FORM_CLOSED]}
        data-form-type={formId.slice(0, 1)}
        {...window.getVariant('layout.execution.form.editGrid')}
      >
        <Grid columns={1} sx={gridSx} data-variant={gridSx.variant}>
          {children}
        </Grid>
      </Box>
    </ConfirmModal>
  );
};

export default ApiaEditGridForm;
